/**
 * SignOffModal — the biological sign-off confirmation (VAL-1 / D-15). Handles BOTH the batch
 * sign-off (the selected worklist rows) and a single result. Enforces the mandatory critical
 * acknowledgment gate CLIENT-side (the server re-checks): a `critique` line cannot be validated
 * until its per-line checkbox is ticked. The decision applies to the whole confirmation set; a
 * reject/reprise sends the lines back to the bench (no ack needed). Keyboard-first: the gate
 * blocks the confirm button + names every un-acked critical.
 *
 * The server is authoritative — even if the UI lets a sign through, the server refuses the WHOLE
 * batch naming every un-acked critical (VALIDATION_ACQUITTEMENT_CRITIQUE_REQUIS, 409). On success
 * the caller maps the SignOutcome[] (escalade ids) into the opened-escalade store.
 */
import { useEffect, useMemo, useState } from "react";
import { Badge, Button, FormField, Modal, Select, Textarea } from "@/ui";
import { useT } from "@/app";
import { token } from "@/design/theme";
import { isCritical, isSansReference, resultValue, type Decision, type WorklistResultat } from "../types";
import { FlagBadge } from "./Badges";

export interface SignOffModalProps {
  open: boolean;
  onClose: () => void;
  /** The result rows to sign (1 = single, N = batch). */
  rows: ReadonlyArray<WorklistResultat>;
  /** Submit handler — receives the per-line ack decisions. Returns a promise; throws on failure. */
  onConfirm: (input: {
    decision: Decision;
    commentaire: string;
    /** result id -> acquittement_critique (only meaningful for critique lines). */
    acks: Record<string, boolean>;
    /** result id -> acquittement_sans_reference (EG-46 — unrangeable lines only). */
    acksSansRef: Record<string, boolean>;
  }) => Promise<void>;
  submitting?: boolean;
}

export function SignOffModal({ open, onClose, rows, onConfirm, submitting }: SignOffModalProps) {
  const { t } = useT();
  const [decision, setDecision] = useState<Decision>("valide");
  const [commentaire, setCommentaire] = useState("");
  const [acks, setAcks] = useState<Record<string, boolean>>({});
  const [acksSansRef, setAcksSansRef] = useState<Record<string, boolean>>({});

  // Reset transient state every time the modal opens for a fresh set.
  useEffect(() => {
    if (open) {
      setDecision("valide");
      setCommentaire("");
      setAcks({});
      setAcksSansRef({});
    }
  }, [open, rows]);

  const criticals = useMemo(() => rows.filter(isCritical), [rows]);
  const sansRefs = useMemo(() => rows.filter(isSansReference), [rows]);

  // The ack gates only apply to a `valide` decision (a reject/reprise needs no ack).
  const unacked = useMemo(
    () => (decision === "valide" ? criticals.filter((r) => acks[String(r.id)] !== true) : []),
    [decision, criticals, acks],
  );
  // EG-46 (R3-H11) — the unrangeable lines carry their own explicit acknowledgment.
  const unackedSansRef = useMemo(
    () => (decision === "valide" ? sansRefs.filter((r) => acksSansRef[String(r.id)] !== true) : []),
    [decision, sansRefs, acksSansRef],
  );
  const gateBlocked = unacked.length > 0 || unackedSansRef.length > 0;

  const decisionOptions = [
    { value: "valide", label: t("validation.sign.decision.valide") },
    { value: "repris", label: t("validation.sign.decision.repris") },
    { value: "rejete", label: t("validation.sign.decision.rejete") },
  ];

  const submit = async () => {
    if (gateBlocked || submitting) return;
    await onConfirm({ decision, commentaire: commentaire.trim(), acks, acksSansRef });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("validation.sign.title")}
      dismissable={!submitting}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t("validation.cancel")}
          </Button>
          <Button
            variant={decision === "valide" ? "primary" : "danger"}
            onClick={() => void submit()}
            loading={submitting === true}
            disabled={gateBlocked}
            aria-describedby={gateBlocked ? "nv-sign-gate" : undefined}
          >
            {t("validation.sign.confirm")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
        <FormField label={t("validation.sign.decision")}>
          {(p) => (
            <Select
              {...p}
              value={decision}
              options={decisionOptions}
              onChange={(e) => setDecision(e.target.value as Decision)}
            />
          )}
        </FormField>

        {/* Summary of the set being signed. */}
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: token.s2,
            maxHeight: 220,
            overflow: "auto",
          }}
        >
          {rows.map((r) => {
            const critical = isCritical(r);
            const sansRef = isSansReference(r);
            const checked = acks[String(r.id)] === true;
            const checkedSansRef = acksSansRef[String(r.id)] === true;
            return (
              <li
                key={String(r.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: token.s3,
                  flexWrap: "wrap",
                  padding: token.s2,
                  borderRadius: token.rSm,
                  border: `1px solid ${token.lineSoft}`,
                  background: critical ? token.tintCritical : token.surface,
                }}
              >
                <span className="nv-mono nv-ltr-island" style={{ color: token.slate, minInlineSize: "4ch" }}>
                  #{String(r.id)}
                </span>
                <span className="nv-mono nv-ltr-island" style={{ fontWeight: 600 }}>
                  {resultValue(r)}
                  {r.unite ? ` ${r.unite}` : ""}
                </span>
                <FlagBadge drapeau={r.drapeau} />
                {sansRef && <Badge tone="hold">{t("validation.sign.sansRef.badge")}</Badge>}
                {critical && decision === "valide" && (
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: token.s2,
                      marginInlineStart: "auto",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setAcks((a) => ({ ...a, [String(r.id)]: e.target.checked }))}
                      aria-label={`${t("validation.sign.ackOne")} #${String(r.id)}`}
                    />
                    <span style={{ fontSize: token.fsLabel }}>{t("validation.sign.ackOne")}</span>
                  </label>
                )}
                {sansRef && decision === "valide" && (
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: token.s2,
                      marginInlineStart: "auto",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checkedSansRef}
                      onChange={(e) => setAcksSansRef((a) => ({ ...a, [String(r.id)]: e.target.checked }))}
                      aria-label={`${t("validation.sign.sansRef.ackOne")} #${String(r.id)}`}
                    />
                    <span style={{ fontSize: token.fsLabel }}>{t("validation.sign.sansRef.ackOne")}</span>
                  </label>
                )}
              </li>
            );
          })}
        </ul>

        {decision === "valide" && sansRefs.length > 0 && (
          <div
            role="note"
            style={{
              padding: token.s3,
              borderRadius: token.rSm,
              border: `1px solid ${token.lineSoft}`,
              background: token.tintHold,
              color: token.ink,
            }}
          >
            <strong style={{ display: "block", marginBlockEnd: token.s1 }}>
              <span aria-hidden>◇ </span>
              {t("validation.sign.sansRefGate.title")}
            </strong>
            <span style={{ fontSize: token.fsLabel }}>{t("validation.sign.sansRefGate.desc")}</span>
          </div>
        )}

        {decision === "valide" && criticals.length > 0 && (
          <div
            role="note"
            style={{
              padding: token.s3,
              borderRadius: token.rSm,
              border: `1px solid ${token.critical}`,
              background: token.tintCritical,
              color: token.ink,
            }}
          >
            <strong style={{ display: "block", marginBlockEnd: token.s1 }}>
              <span aria-hidden>◆ </span>
              {t("validation.sign.criticalGate.title")}
            </strong>
            <span style={{ fontSize: token.fsLabel }}>{t("validation.sign.criticalGate.desc")}</span>
          </div>
        )}

        <FormField label={t("validation.sign.comment")} hint={t("validation.sign.comment.hint")}>
          {(p) => (
            <Textarea
              {...p}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              rows={3}
            />
          )}
        </FormField>

        <p style={{ fontSize: token.fsCaption, color: token.steel, margin: 0 }}>
          {t("validation.sign.immutableNote")}
        </p>

        {gateBlocked && (
          <p id="nv-sign-gate" role="alert" style={{ margin: 0, color: token.critical, fontSize: token.fsLabel }}>
            {unacked.length > 0 && <Badge tone="critical">{t("validation.sign.ackRequired", { n: unacked.length })}</Badge>}{" "}
            {unackedSansRef.length > 0 && (
              <Badge tone="hold">{t("validation.sign.sansRef.ackRequired", { n: unackedSansRef.length })}</Badge>
            )}
          </p>
        )}
      </div>
    </Modal>
  );
}
